/**
 * Logo & favicon upload specs + client-side processing.
 *
 * Assets are uploaded in-app (not pasted as URLs): the picker reads the file,
 * processes it on a canvas, and stores a base64 data URL that is PATCHed onto
 * org_config. The server re-validates at the boundary
 * (services/api/src/domain/org_config.py::validate_branding_asset) — keep the
 * limits here in sync with _MAX_ASSET_BYTES there.
 *
 * Raster only (PNG/JPEG/WebP). SVG is intentionally excluded: an SVG rendered
 * from a data URL is a script-in-image XSS surface.
 *
 * Favicons are DERIVED, not required-square: any image (including a logo you
 * already uploaded) is centre-fit onto a small square PNG canvas, so there's
 * never a "must be square" dead end.
 */

export interface AssetSpec {
  label: string
  accept: string
  mimeTypes: readonly string[]
  /** Max size of the *picked file* (before any downscaling). */
  maxBytes: number
  /** Requirement copy shown under the field. */
  help: string
  /** Logos are downscaled to fit this box (px) if larger. */
  maxPx?: number
}

const RASTER_TYPES = ['image/png', 'image/jpeg', 'image/webp'] as const
const RASTER_ACCEPT = RASTER_TYPES.join(',')

export const LOGO_SPEC: AssetSpec = {
  label: 'Logo',
  accept: RASTER_ACCEPT,
  mimeTypes: RASTER_TYPES,
  maxBytes: 512 * 1024,
  maxPx: 1024,
  help: 'PNG, JPEG or WebP, up to 512 KB. Transparent PNG/WebP recommended. Larger images are scaled down.',
}

export const FAVICON_SPEC: AssetSpec = {
  label: 'Favicon',
  accept: RASTER_ACCEPT,
  mimeTypes: RASTER_TYPES,
  maxBytes: 512 * 1024,
  help: 'Any image (PNG, JPEG or WebP, up to 512 KB). We crop it to a square browser-tab icon — or reuse a logo above.',
}

/** Size of the generated favicon square (px). 64 is crisp on retina tabs. */
const FAVICON_PX = 64

/** Type + size check. Pure (no DOM) so it is unit-testable. Returns an error
 *  message, or null when the file passes. */
export function checkFileMeta(file: File, spec: AssetSpec): string | null {
  if (!spec.mimeTypes.includes(file.type)) {
    return `Unsupported file type. ${spec.help}`
  }
  if (file.size > spec.maxBytes) {
    return `File is too large (${Math.ceil(file.size / 1024)} KB). ${spec.help}`
  }
  return null
}

/** Validate + read a logo file, downscaling to `maxPx` if oversized. Returns a
 *  data URL. Throws a user-facing message on a bad type/size. */
export async function prepareLogo(file: File): Promise<string> {
  const metaError = checkFileMeta(file, LOGO_SPEC)
  if (metaError) throw new Error(metaError)
  const dataUrl = await readAsDataUrl(file)
  return fitWithin(dataUrl, LOGO_SPEC.maxPx ?? 1024)
}

/** Validate + read a favicon file, then crop it to a square icon. */
export async function prepareFaviconFromFile(file: File): Promise<string> {
  const metaError = checkFileMeta(file, FAVICON_SPEC)
  if (metaError) throw new Error(metaError)
  const dataUrl = await readAsDataUrl(file)
  return squareIcon(dataUrl)
}

/** Derive a favicon from an already-set logo (data URL). */
export async function faviconFromImage(src: string): Promise<string> {
  try {
    return await squareIcon(src)
  } catch {
    // Cross-origin https logos taint the canvas; a same-origin data URL won't.
    throw new Error('Could not build a favicon from that logo. Upload an image instead.')
  }
}

// ── internals ──────────────────────────────────────────────────────────────

function readAsDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result))
    reader.onerror = () => reject(new Error('Could not read the file.'))
    reader.readAsDataURL(file)
  })
}

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = () => reject(new Error('Could not decode the image.'))
    img.src = src
  })
}

/** Centre-fit `src` onto a transparent FAVICON_PX square and return a PNG. */
async function squareIcon(src: string): Promise<string> {
  const img = await loadImage(src)
  const canvas = document.createElement('canvas')
  canvas.width = FAVICON_PX
  canvas.height = FAVICON_PX
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not available.')
  const scale = Math.min(FAVICON_PX / img.naturalWidth, FAVICON_PX / img.naturalHeight)
  const w = img.naturalWidth * scale
  const h = img.naturalHeight * scale
  ctx.drawImage(img, (FAVICON_PX - w) / 2, (FAVICON_PX - h) / 2, w, h)
  return canvas.toDataURL('image/png')
}

/** Return `src` unchanged if within `maxPx`, else contain-downscale to a PNG. */
async function fitWithin(src: string, maxPx: number): Promise<string> {
  const img = await loadImage(src)
  if (img.naturalWidth <= maxPx && img.naturalHeight <= maxPx) return src
  const scale = Math.min(maxPx / img.naturalWidth, maxPx / img.naturalHeight)
  const w = Math.round(img.naturalWidth * scale)
  const h = Math.round(img.naturalHeight * scale)
  const canvas = document.createElement('canvas')
  canvas.width = w
  canvas.height = h
  const ctx = canvas.getContext('2d')
  if (!ctx) throw new Error('Canvas not available.')
  ctx.drawImage(img, 0, 0, w, h)
  return canvas.toDataURL('image/png')
}
